// *******************************************************************************************
// How token interceptor is working here ==>
// Once upon a time,
// Just after SSO login, backend API will called, which gives token to stored in local storage.
// Recieved token from backend will have expiry 30 minutes.
// We will pass token as Autherization in every API.
// If token is expired then we will recieve 401 error code.
// Once 401 error recived will collect all API having error code 401 (ie. un-Autherized) and store them in array (failedQueue) .
// After completion of storing failed API. We will call 'getAccessToken' Api to create fresh token and replace the oldd with new one in local Storage
// Once we recived new valid token, we are looping array (ie. failedQueue) having failed API with new autherization token one by one.
// Thank you for your attention. :)
// *******************************************************************************************

import axios from 'axios';
import { getAccessToken } from '@services/authServices';
import { navigateTo } from './navigationService';
import { appInsights } from '@services/appInsightsService';
import { SOURCE } from '@constants/commonConstants';

let failedQueue = [];
let isRefreshing = false;

// Universal axios instance to be used in every API call
// This will be only one Instance throughtout of project except getAccessToken API
const options = {
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
    credentials: 'include',
    'Access-Control-Allow-Methods': '*',
  },
};

// To get access token stored in local storage
const getCurrentAccessToken = () => {
  const authDetailsFetched = JSON.parse(sessionStorage.getItem('authDetails'));
  return authDetailsFetched.token;
};

// To set newly generated access token in local storage
const setRefreshedTokens = newAuthDetails => {
  sessionStorage.setItem('authDetails', JSON.stringify(newAuthDetails));
};

// To make que of failed APIs due token expiry or invalid token
const processQueue = error => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

const client = axios.create(options);

// Request Interceptor

client.interceptors.request.use(
  config => {
    if (
      localStorage.getItem('isSuperUser') === 'false' ||
      localStorage.getItem('targetUserEmail') === 'null' ||
      localStorage.getItem('targetUserEmail') === undefined ||
      config.url === '/user/checksuperuser' ||
      (localStorage.getItem('checkingForRoleList') === 'true' &&
        (config.url === 'user/dashboard/profiledetails' || config.url === 'user/identifypersona'))
    ) {
      config.data = {
        ...config.data,
      };
    } else {
      // Check if this is a PL/GCSP iframe/opportunity name and comemnt related API call, MSL new tab account name operation, GCSP hyperlink operation(CRMS and LENS), or MSL hyperlink operation(CRM BI Dashboard)
      // Use userEmail for PL/GCSP iframe/opportunity name and comment operations, MSL new tab account name operations, GCSP hyperlink operations(CRMS and LENS), and MSL hyperlink operations (CRM BI Dashboard), targetUserEmail for other super user operations
      const isIframeRelatedCall = localStorage.getItem('isIframeOperation') === 'true';
      const isMslNewTabOperation = localStorage.getItem('isMslNewTabOperation') === 'true';
      const isGcspHyperlinkOperation = localStorage.getItem('isGcspHyperlinkOperation') === 'true';
      const isMslHyperlinkOperation = localStorage.getItem('isMslHyperlinkOperation') === 'true';

      config.data = {
        ...config.data,
        //Super User- iframe/new tab/hyperlink operation(PL Opp Name and Comments, GCSP Opp Name CRM and Lens, MSL CRM BI Dashboard) uses userEmail when flags are true else uses targetUserEmail in case of false
        email:
          isIframeRelatedCall ||
          isMslNewTabOperation ||
          isGcspHyperlinkOperation ||
          isMslHyperlinkOperation
            ? localStorage.getItem('userEmail')
            : localStorage.getItem('targetUserEmail'),
      };
    }

    if (config.authorization !== false) {
      const token = getCurrentAccessToken();
      const URL = config?.baseURL + config?.url;

      if (token) {
        // Inserting token in header
        config.headers.Authorization = token;
        config.headers.email = localStorage.getItem('userEmail');

        appInsights.trackTrace({
          message: JSON.stringify({
            message: `Token set successfully in request: ${URL}`,
            userId: localStorage.getItem('userEmail'),
            source: SOURCE.FRONTEND,
          }),
          severityLevel: 1, // 1 represents response log
        });
      }
    } else {
      appInsights.trackTrace({
        message: JSON.stringify({
          error: `Error setting token in request: ${URL}`,
          userId: localStorage.getItem('userEmail'),
          source: SOURCE.FRONTEND,
        }),
        severityLevel: 3, // 3 represents failure
      });
    }
    return config;
  },
  error => {
    appInsights.trackTrace({
      message: JSON.stringify({
        error: error,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 3, // 3 represents failure
    });

    return Promise.reject(error);
  },
);

// Response Interceptor
client.interceptors.response.use(
  response => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    // if (response.status === 203) {
    //   navigateTo('/forbidden-access');
    // }
    const URL = response?.config?.baseURL + response?.config?.url;
    appInsights.trackTrace({
      message: JSON.stringify({
        message: `API Call: ${URL}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 1, // 1 represents response log
    });

    return response;
  },
  error => {
    const originalRequest = error.config;
    // If error, process all the requests in the queue and logout the user.
    const handleError = error => {
      processQueue(error);
      // Logout functionality can be implement here
      return Promise.reject(error);
    };

    const URL = error?.config?.baseURL + error?.config?.url;
    appInsights.trackTrace({
      message: JSON.stringify({
        message: `Error Calling API: ${URL}`,
        error: `Error: ${error}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 3, // 3 represents failure
    });

    if (error.response?.status === 403) {
      navigateTo('/forbidden-access');
      return Promise.resolve(error);
    }

    // if (error.response?.status === 400) {
    //   navigateTo('no-user-found');
    //   return Promise.reject(error);
    // }

    // Refresh token conditions
    if (error.response?.status === 401 && originalRequest?._retry !== true) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return client(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
      isRefreshing = true;
      originalRequest._retry = true;
      return getAccessToken(localStorage.getItem('userEmail'), sessionStorage.getItem('azureToken'))
        .then(res => {
          setRefreshedTokens(res);
          processQueue(null);
          return client(originalRequest);
        }, handleError)
        .finally(() => {
          isRefreshing = false;
        });
    }

    // Refresh token missing or expired => logout session/user...
    // if (error.response?.status === 401 && error.response?.data?.message === 'TokenExpiredError') {
    //   return handleError(error);
    // }

    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  },
);

export default client;
